import axios, { AxiosResponse } from "axios";
import { handleResponse } from "go-core/api/handleResponse";
import { parseApiData } from "go-core/api/parseApiData";
import { ItemResponse } from "go-core/api/types";
import { getApiURL } from "../../utils/envutil";
import { organizationApi } from "../Api/Organization/organizationApi";
import { InvitationApi } from "../Api/Organization/organizationTypes";
import { userApi } from "../Api/User/userApi";
import { ConfigApi } from "../Api/types";
import applyAppTokenInterceptor from "./applyAppTokenInterceptor";
import applyAppTokenRefreshInterceptor from "./applyAppTokenRefreshInterceptor";
import { ResetPasswordApi } from "./types";

const restApi = axios.create({
	withCredentials: true,
});

applyAppTokenInterceptor(restApi);
applyAppTokenRefreshInterceptor(restApi);

const noAuthApi = axios.create({
	withCredentials: true,
	baseURL: `${getApiURL()}/ajax`,
});

class api {
	static getAxios = () => {
		return restApi;
	};

	static organization = () => {
		return organizationApi;
	};
	static user = () => {
		return userApi;
	};

	static async getConfig(): Promise<AxiosResponse<ItemResponse<ConfigApi>>> {
		return axios.get("/ajax/config", {
			baseURL: getApiURL(),
			withCredentials: true,
		});
	}

	static async getClientDataByTaxIdNo(tax_id_no: string): Promise<AxiosResponse> {
		return restApi.get(`/gus_client_info?tax_id_no=${tax_id_no}`);
	}

	static async forgotPassword(email: string): Promise<AxiosResponse> {
		return noAuthApi.post(`/users/forgot_password?email=${email}`).catch((err) => {
			return handleResponse(err.response);
		});
	}

	static async resetPassword(data: ResetPasswordApi): Promise<AxiosResponse> {
		return noAuthApi.post(`/users/reset_password`, data).catch((err) => {
			return handleResponse(err.response);
		});
	}

	static async registerUserByInvitation(data: any): Promise<AxiosResponse> {
		return noAuthApi.post("/login/register_by_invitation", data).catch((err) => {
			return handleResponse(err.response);
		});
	}

	static async registerUser(data: any): Promise<AxiosResponse> {
		return noAuthApi.post("/login/register", data).catch((err) => {
			return handleResponse(err.response);
		});
	}

	static async getLoginInvitation(
		acceptationCode: string,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<InvitationApi> {
		return parseApiData(
			restApi.get(`/login/invitations/${acceptationCode}`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	//MESSAGE EVENTS
	static async getMessageEvents(params?: Record<string, any>, options?: any): Promise<AxiosResponse> {
		return restApi.get("/message_events", { params, cancelToken: options?.cancelToken });
	}

	static async getMessageEvent(id: string, params?: Record<string, any>): Promise<AxiosResponse> {
		return restApi.get(`/message_events/${id}`, { params });
	}

	static async getMessageEventTypes(params?: Record<string, any>): Promise<string[]> {
		return parseApiData(restApi.get(`/message_events/types`, { params }));
	}

	static async getRequestItems(params?: Record<string, any>, options?: any): Promise<AxiosResponse> {
		return restApi.get("/requests", { params, cancelToken: options?.cancelToken });
	}

	static async getRequestItem(id: string, params?: Record<string, any>): Promise<AxiosResponse> {
		return restApi.get(`/requests/${id}`, { params });
	}

	static async retryRequestItem(id: string, params?: Record<string, any>): Promise<AxiosResponse> {
		return restApi.post(`/requests/${id}/retry`, null, { params });
	}

	static getRequestItemTypes(): Promise<string[]> {
		return parseApiData(restApi.get(`/requests/types`));
	}

	// JOB
	static async getJobs(params?: Record<string, any>, options?: any): Promise<AxiosResponse> {
		return restApi.get("/jobs", { params, cancelToken: options?.cancelToken });
	}

	static async getJob(id: string, params?: Record<string, any>): Promise<AxiosResponse> {
		return restApi.get(`/jobs/${id}`, { params });
	}

	static async cancelJob(id: string): Promise<AxiosResponse> {
		return restApi.post(`/jobs/${id}`);
	}

	static getJobNames(): Promise<string[]> {
		return parseApiData(restApi.get(`jobs/names`));
	}
}

export { restApi, api };
